import {useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Download01Icon} from '@/components/ui/icon/Icon'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {useGetPriTxtAvailableMonths} from '@/features/accesses/activity-report/queries/useGetPriTxtAvailableMonths'
import {useExportPriTxtByMonth} from '@/features/accesses/activity-report/queries/useExportPriTxtByMonth'
import {
    formatMonthPeriodLabel,
    periodToMonthYear,
    resolveActivityReportErrorLabel
} from '@/features/accesses/activity-report/utils'
import {ExportCardShell} from './ExportCardShell'
import {StyledCardFooter, StyledIconChip, StyledSelectWrapper, StyledTxtBadge} from './style'

// Operational card that generates and downloads the DSP Flux (PRI txt) ZIP for a chosen month —
// no row selection required. The month select is fed by the backend availability list and
// pre-selected to the newest exportable period.
export const DspFluxExportCard = () => {
    const {t} = useTranslation()
    const {data, isPending: isLoadingMonths} = useGetPriTxtAvailableMonths()
    const months = data?.months ?? []
    const hasMonths = months.length > 0

    const [selectedMonthChoice, setSelectedMonthChoice] = useState<string>()
    const selectedMonth = selectedMonthChoice && months.includes(selectedMonthChoice) ? selectedMonthChoice : months[0]

    const exportMutation = useExportPriTxtByMonth({
        onSuccess: () => toast.success(t('dsp_flux_export:export_success')),
        // The blob-error interceptor converts the 500 body to JSON, so `code` is available here.
        onError: error => toast.error(t(resolveActivityReportErrorLabel(error.response?.data?.code)))
    })

    const handleDownload = () => {
        // No duplicate submissions while a request is in flight.
        if (!selectedMonth || exportMutation.isPending) return
        exportMutation.mutate(periodToMonthYear(selectedMonth))
    }

    return (
        <ExportCardShell
            icon={
                <StyledIconChip $tone="success">
                    <StyledTxtBadge>.TXT</StyledTxtBadge>
                </StyledIconChip>
            }
            title={t('dsp_flux_export:dsp_card_title')}
        >
            <StyledCardFooter>
                <StyledSelectWrapper>
                    <InputSelect
                        value={selectedMonth}
                        optionsEntries={months.map((month): [string, string] => [month, formatMonthPeriodLabel(month)])}
                        onChange={value => setSelectedMonthChoice(value)}
                        disabled={isLoadingMonths || !hasMonths}
                        placeholder={
                            isLoadingMonths ? t('commons:loading') : t('dsp_flux_export:month_select_placeholder')
                        }
                    />
                </StyledSelectWrapper>
                <Button
                    variant="primaryNeutral"
                    onClick={handleDownload}
                    disabled={!selectedMonth || exportMutation.isPending}
                >
                    {exportMutation.isPending ? <Spinner size={16} /> : <Download01Icon size={16} />}
                    {t('dsp_flux_export:download_cta')}
                </Button>
            </StyledCardFooter>
        </ExportCardShell>
    )
}
